import { API_CONFIG } from '@/config/api';
import { useEffect, useRef, useState } from 'react';

type GoogleCredentialResponse = {
  credential?: string;
};

type GoogleButtonText = 'signin_with' | 'signup_with' | 'continue_with';

type GoogleSignInButtonProps = {
  disabled?: boolean;
  text?: GoogleButtonText;
  onCredential: (credential: string) => void;
  onError?: (message: string) => void;
};

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: GoogleCredentialResponse) => void;
            ux_mode?: 'popup' | 'redirect';
          }) => void;
          renderButton: (
            parent: HTMLElement,
            options: {
              theme?: 'outline' | 'filled_blue' | 'filled_black';
              size?: 'large' | 'medium' | 'small';
              shape?: 'rectangular' | 'pill' | 'circle' | 'square';
              text?: GoogleButtonText;
              width?: number;
            }
          ) => void;
        };
      };
    };
  }
}

const GOOGLE_SCRIPT_ID = 'google-identity-services';
const GOOGLE_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

function loadGoogleScript(): Promise<void> {
  const existing = document.getElementById(GOOGLE_SCRIPT_ID) as HTMLScriptElement | null;
  if (existing) {
    return new Promise((resolve, reject) => {
      if (window.google?.accounts?.id) {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Google 登录脚本加载失败')), {
        once: true,
      });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = GOOGLE_SCRIPT_ID;
    script.src = GOOGLE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Google 登录脚本加载失败'));
    document.head.appendChild(script);
  });
}

export function GoogleSignInButton({
  disabled = false,
  text = 'signin_with',
  onCredential,
  onError,
}: GoogleSignInButtonProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [scriptReady, setScriptReady] = useState(false);

  useEffect(() => {
    let alive = true;
    loadGoogleScript()
      .then(() => {
        if (alive) setScriptReady(true);
      })
      .catch((e) => {
        if (alive) onError?.(e instanceof Error ? e.message : 'Google 登录不可用');
      });
    return () => {
      alive = false;
    };
  }, [onError]);

  useEffect(() => {
    const host = hostRef.current;
    const googleId = window.google?.accounts?.id;
    const clientId = API_CONFIG.GOOGLE_CLIENT_ID.trim();
    if (!scriptReady || !host || !googleId || !clientId) return;

    host.innerHTML = '';
    googleId.initialize({
      client_id: clientId,
      ux_mode: 'popup',
      callback: (response) => {
        if (disabled) return;
        if (!response.credential) {
          onError?.('Google 登录未返回凭证');
          return;
        }
        onCredential(response.credential);
      },
    });
    googleId.renderButton(host, {
      theme: 'outline',
      size: 'large',
      shape: 'rectangular',
      text,
      width: Math.min(Math.max(host.clientWidth, 240), 400),
    });
  }, [disabled, onCredential, onError, scriptReady, text]);

  return (
    <div
      className="google-signin-host"
      ref={hostRef}
      style={{ opacity: disabled ? 0.65 : 1, pointerEvents: disabled ? 'none' : 'auto' }}
    />
  );
}
